<template>
  <div
    v-if="isButtonConfigured"
    class="template-landing-button-view"
    :class="{ 'template-landing-button-view--full-width': props.fullWidth }"
  >
    <BaseButton
      variant="primary"
      size="md"
      rounded
      :color="props.button.color"
      :text-color="filledTextColor"
      :with-full="props.fullWidth"
      class="template-landing-button-view__button"
      @click="handleClick"
    >
      {{ props.button.label }}
    </BaseButton>
  </div>
</template>

<script setup lang="ts">
import type { ILandingTemplateButton } from '~/entities/projects/model/landingTemplateButtons'
import {

  isLandingPlacementButtonConfigured,
} from '~/entities/projects/model/landingTemplateButtons'
import { navigateToLandingModuleButton } from '~/entities/projects/model/navigateToLandingModuleButton'
import { useProjectRouteRef } from '~/entities/projects/model/useProjectRouteRef'
import { isHexColorDark, parseHexColor } from '~/shared/lib'
import { BaseButton } from '~/shared/ui'

interface Props {
  button: ILandingTemplateButton
  fullWidth?: boolean
  projectId?: string
  whenNavigate?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  projectId: '',
  whenNavigate: undefined,
})

const route = useRoute()
const { projectId, projectSlug, routeParamForLinks } = useProjectRouteRef(route)

const resolvedProjectId = computed(
  () => props.projectId || projectId.value || '',
)

const navigationProject = computed(() => {
  if (props.projectId) {
    return {
      id: props.projectId,
      slug: projectSlug.value,
    }
  }

  return {
    id: projectId.value,
    slug: projectSlug.value,
  }
})

const isButtonConfigured = computed(() =>
  isLandingPlacementButtonConfigured(props.button),
)

const filledTextColor = computed(() => {
  const color = props.button.color?.trim()
  if (color && parseHexColor(color) && !isHexColorDark(color)) {
    return 'var(--color-foreground-regular)'
  }
  return 'var(--color-foreground-light)'
})

function handleClick() {
  if (!resolvedProjectId.value) {
    return
  }

  navigateToLandingModuleButton(
    routeParamForLinks.value || navigationProject.value,
    props.button,
  )
  props.whenNavigate?.()
}
</script>

<style lang="scss" scoped>
.template-landing-button-view {
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  align-self: flex-start;

  &__button {
    border-radius: rem(14px);
    max-width: 100%;
    min-width: 0;

    > p {
      text-align: left;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &--full-width {
    width: 100%;
    align-self: stretch;

    .template-landing-button-view__button {
      width: 100%;
    }
  }

  @include bp-media('xs', 'sm') {
    width: 100%;
    align-self: stretch;

    .template-landing-button-view__button {
      width: 100%;
    }
  }
}
</style>
